import React, { useState } from 'react'
import addHours from 'date-fns/addHours'
import endOfTomorrow from 'date-fns/endOfTomorrow'
import { useLocation } from 'wouter'
import * as dayjs from 'dayjs'

import { DatePicker } from '../../common/date'
import { Form, Button, Input, stringToSlug } from '@guillotinaweb/react-gmi'
import { setLangs, LANGS, SELLING_COUNTRIES } from '../../consts'
import { toISOStringWithTimezone } from '../../utils'
import { Switch } from 'antd'
import { ProductSelector } from '../../components/product-selector'
import { getProductMask } from '../../utils'

const tomorrow = addHours(endOfTomorrow(), 2)

export function NewsletterForm({ onSubmit }) {
  const [id, setId] = useState('')
  const [campaign, setCampaign] = useState('')
  const [date, setDate] = useState(tomorrow.toISOString())
  const [loading, setLoading] = useState(false)
  const [productDisplay, setProductDisplay] = useState(false)
  const [product, setProduct] = useState()
  const [, navigate] = useLocation()
  const dateString = dayjs(date).format('D_MM_YY_')

  async function submit() {
    setLoading(true)

    await onSubmit({
      '@type': 'Newsletter',
      id: dateString + id,
      subject: setLangs(''),
      title: campaign,
      contenido:
        product?.length > 0 && productDisplay
          ? await buildProductContent(product[0])
          : [],
      schedule_at: date,
      sendto: {
        langs: LANGS,
        countries: SELLING_COUNTRIES,
        frequency: 1,
      },
    })
    setLoading(false)
    navigate(`/?path=/newsletters/${dateString + id}/`)
  }

  return (
    <Form onSubmit={submit}>
      <h1 className="title is-size-4 has-text-primary">Add Newsletter</h1>

      <label className="label">Id:</label>
      <div style={{ display: 'flex' }}>
        <div style={{ width: 130 }}>
          <Input value={dateString} disabled />
        </div>
        <div style={{ width: '100%' }}>
          <Input
            required
            placeholder="Id suffix"
            onChange={(v) => setId(stringToSlug(v))}
            value={id}
          />
        </div>
      </div>
      <label className="label">Campaign name:</label>
      <Input
        required
        placeholder="Campaign name"
        onChange={setCampaign}
        value={campaign}
      />

      <DatePicker
        value={date}
        name="Sending date"
        onUpdate={(e) => setDate(toISOStringWithTimezone(e.target.value))}
      />

      <hr />
      <Switch
        checkedChildren="Product Newsletter"
        unCheckedChildren="Product Newsletter"
        onChange={setProductDisplay}
      />

      {productDisplay && (
        <ProductSelector
          id={product}
          state={['selling']}
          products={product}
          onChange={setProduct}
        />
      )}

      <div style={{ marginTop: 20 }} className="level level-right">
        <Button className="is-primary" loading={loading}>
          Create and continue editing the newsletter
        </Button>
      </div>
    </Form>
  )
}

function weekday(lang) {
  const formatter = Intl.DateTimeFormat(lang, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  return formatter.format(new Date())
}

async function buildProductContent(product_id) {
  const prod = await getProduct(product_id)
  const option = prod.opciones[0]
  const data = { id: prod.id, type: prod.tipo }

  let content = [
    {
      id_tipo: 'h1',
      es: prod.lema?.es || '',
      fr: prod.lema?.fr || '',
      de: prod.lema?.de || '',
      it: prod.lema?.it || '',
      en: prod.lema?.en || '',
    },
    {
      id_tipo: 'bd',
      product_kind: 'full',
      referencia: option.referencia,
      bg_color_img: '#e9edeb',
      bg_color_product: '#e9edeb',
      alt: setLangs(''),
      image_src: setLangs(''),
      product_side: 'left',
    },
    {
      id_tipo: 'h2',
      es: '',
      fr: '',
      de: '',
      it: '',
      en: '',
    },
    {
      id_tipo: 'p',
      es: prod.description?.es || '',
      fr: prod.description?.fr || '',
      de: prod.description?.de || '',
      it: prod.description?.it || '',
      en: prod.description?.en || '',
    },
    {
      id_tipo: 'h2',
      es: prod.otras_props?.a_que_sabe?.es ? '¿A qué sabe este vino?' : '',
      fr: prod.otras_props?.a_que_sabe?.fr
        ? 'Quel est le goût de ce vin ?'
        : '',
      de: prod.otras_props?.a_que_sabe?.de ? 'Wie schmeckt dieser Wein?' : '',
      it: prod.otras_props?.a_que_sabe?.it ? 'Che sapore ha questo vino?' : '',
      en: prod.otras_props?.a_que_sabe?.en
        ? 'What does this wine taste like?'
        : '',
    },
    {
      id_tipo: 'p',
      es: prod.otras_props?.a_que_sabe?.es || '',
      fr: prod.otras_props?.a_que_sabe?.fr || '',
      de: prod.otras_props?.a_que_sabe?.de || '',
      it: prod.otras_props?.a_que_sabe?.it || '',
      en: prod.otras_props?.a_que_sabe?.en || '',
    },
    {
      id_tipo: 'h2',
      es: '',
      fr: '',
      de: '',
      it: '',
      en: '',
    },
    {
      id_tipo: 'tri_down',
      text: {
        es: '¡Solo hoy en oferta!',
        fr: "En promotion aujourd'hui seulement !",
        de: 'Nur heute im Angebot!',
        it: 'Solo oggi in offerta!',
        en: 'On offer today only!',
      },
      path: {
        es: getProductMask(data, 'es'),
        fr: getProductMask(data, 'fr'),
        de: getProductMask(data, 'de'),
        it: getProductMask(data, 'it'),
        en: getProductMask(data, 'en'),
      },
      btn_text: {
        es: 'COMPRAR',
        fr: 'ACHETER',
        de: 'KAUFEN',
        it: 'ACQUISTA',
        en: 'BUY',
      },
    },
    {
      id_tipo: 'w',
      es: `Oferta válida solo hoy, ${weekday(
        'es'
      )}, o hasta agotar existencias.`,
      fr: `Offre valable uniquement aujourd'hui, ${weekday(
        'fr'
      )}, ou jusqu'à épuisement des stocks.`,
      de: `Angebot gültig nur heute, ${weekday(
        'de'
      )} oder solange der Vorrat reicht.`,
      it: `Offerta valida solo oggi, ${weekday(
        'it'
      )}, o ad esaurimento scorte.`,
      en: `Offer valid only today, ${weekday('en')}, or while stocks last.`,
    },
  ]
  return content
}

async function getProduct(group) {
  const resp = await fetch(`/db/guillotina/@product?id=${group}`)
  const prod = await resp.json()
  return prod.product
}
